import {
  WaitingListHistoryEvent,
  WaitingListReleaseOrder
} from '@prisma/client'
import { z } from 'zod'
import {
  waitingListAdditionalFields,
  waitingListRequiredFields
} from 'components/Dashboard/WaitingLists/waitingListRaceConfig.utils'
import { Filter } from 'domain/models/AttendeeSortField/AttendeeSortField'
import { AttendeeId, RaceId } from 'domain/models/ValueObject/EntityIds'

export const WaitingListOrder = {
  REGISTRATION_DATE: 'REGISTRATION_DATE',
  RANDOM: 'RANDOM'
} as const

export type WaitingListOrder = keyof typeof WaitingListOrder

export interface WaitingListsRace {
  id: RaceId
  raceName: string
  displayOrder: number | null
  attendeesCount: number
  enabled: boolean
  delay?: number | null
  order?: WaitingListOrder | null
  autoRelease?: boolean | null
  additionalFields: string[]
  teamNameRequired: boolean
}

interface RaceWaitingListConfiguration {
  enabled: boolean
  delay: number | null
  releaseOrder: WaitingListReleaseOrder | null
  additionalFields: string[]
}

export interface AwaitingRegistrationsRace {
  id: number
  name: string
  raceWaitingListConfiguration: RaceWaitingListConfiguration | null
  attendeesCount: number
  isRelay: boolean
}

export const WaitingListsRaceSchema = z.object({
  id: z.number(),
  delay: z.coerce.number().positive(),
  order: z.enum([WaitingListOrder.REGISTRATION_DATE, WaitingListOrder.RANDOM]),
  autoRelease: z.boolean(),
  additionalFields: z.array(
    z.enum([...waitingListRequiredFields, ...waitingListAdditionalFields])
  )
})

export type WaitingListsRaceSchema = z.infer<typeof WaitingListsRaceSchema>

const postalAddressSchema = z.object({
  postalAddressMain: z.string(),
  postalAddressSecondary: z.string(),
  postalAddressZipcode: z.string(),
  postalAddressCity: z.string(),
  postalAddressCountry: z.string(),
  postalAddressAreaLevel1: z.string(),
  postalAddressAreaLevel2: z.string()
})

export const WaitingListAttendeeSchema = (
  isRelay: boolean,
  additionalFields: string[]
) =>
  z.object({
    lastName: z.string().min(1),
    firstName: z.string().min(1),
    email: z.string().min(1).email(),
    relayTeamName: isRelay ? z.string().min(1) : z.string().optional(),
    gender: additionalFields.includes('gender')
      ? z.enum(['MALE', 'FEMALE'])
      : z.enum(['MALE', 'FEMALE']).optional(),
    birthDate: additionalFields.includes('birthDate')
      ? z.coerce.date()
      : z.coerce.date().optional(),
    phoneNumber: additionalFields.includes('phoneNumber')
      ? z.string().min(1)
      : z.string().optional(),
    nationality: additionalFields.includes('nationality')
      ? z.string().min(1)
      : z.string().optional(),
    particulars: additionalFields.includes('postalAddress')
      ? z.object({ address: postalAddressSchema })
      : z.object({}).optional()
  })

export type WaitingListAttendee = z.infer<
  ReturnType<typeof WaitingListAttendeeSchema>
>
export enum ColumnField {
  CREATED_AT = 'createdAt',
  UPDATED_AT = 'updatedAt',
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  EMAIL = 'email',
  TEAM_NAME = 'relayTeamName',
  WAITING_LIST_STATUS = 'waitingListStatus',
  GENDER = 'gender',
  BIRTH_DATE = 'birthDate',
  PHONE_NUMBER = 'phoneNumber',
  NATIONALITY = 'nationality',
  POSTAL_ADDRESS = 'streetNumberAndStreetName'
}

export interface GetAwaitingAttendeesParams {
  raceId: RaceId
  searchQuery?: string | undefined
  sortField: ColumnField | string
  sortDirection: 'asc' | 'desc'
  start: number
  end: number
  filter?: Filter
  displayRegistered?: boolean
}

export interface AwaitingAttendee extends WaitingListAttendee {
  id: AttendeeId
  createdAt: Date
  lastEvent?: string | null
  status?: WaitingListHistoryEvent | null
  updatedAt?: Date | null
}

export type AttendeeToRelease = Omit<AwaitingAttendee, 'draftOrderId'>

export interface GetWithFiltersResponse {
  attendees: AwaitingAttendee[]
  count: number
}
